/**
 * roamingSettings access for the task pane — the mailbox-global store. Owns the
 * connection record (written by the Connect flow). The staged-file queue is
 * per-compose, not mailbox-global, so it lives in sessionData (see
 * staged_queue.js).
 */

const CONNECTION_KEY = "leapfile_connection"; // { session, username } — session is the fts session_oid
const THRESHOLD_KEY = "leapfile_threshold_account"; // { enabled, mb } — mirror of the account's desktopconfiguration row

export function getConnection() {
  try {
    return JSON.parse(Office.context.roamingSettings.get(CONNECTION_KEY) || "null");
  } catch {
    return null;
  }
}

export function getSessionId() {
  const conn = getConnection();
  return conn && conn.session ? conn.session : "";
}

/** Persist the connection record; resolves once roamingSettings.saveAsync completes. */
export function setConnection(record) {
  return new Promise((resolve) => {
    Office.context.roamingSettings.set(CONNECTION_KEY, JSON.stringify(record));
    Office.context.roamingSettings.saveAsync(() => resolve());
  });
}

/**
 * Forget the stored connection (logout / expired session); resolves once saved.
 * Drops the mirrored account threshold with it: roamingSettings is mailbox-global
 * and the next sign-in can be a different LeapFILE account, which must never
 * inherit this one's limit.
 */
export function clearConnection() {
  return new Promise((resolve) => {
    Office.context.roamingSettings.remove(CONNECTION_KEY);
    Office.context.roamingSettings.remove(THRESHOLD_KEY);
    Office.context.roamingSettings.saveAsync(() => resolve());
  });
}

/**
 * The large-attachment threshold: suggest LeapFILE once the draft's own attachments
 * total more than `mb`. Read-only here — the setting lives in the main app under
 * Account > Desktop client management > Outlook settings (the same
 * desktopconfiguration row the classic plugin reads), and the pane only mirrors it.
 * Off whenever the account hasn't turned it on. A configured-but-unusable size
 * falls back to 1 MB, the same fallback the classic plugin applies.
 */
export function getThreshold() {
  let stored = null;
  try {
    stored = JSON.parse(Office.context.roamingSettings.get(THRESHOLD_KEY) || "null");
  } catch {
    stored = null;
  }
  if (!stored || stored.enabled !== true) return { enabled: false, mb: 0 };
  const mb = Number(stored.mb);
  return { enabled: true, mb: mb > 0 ? mb : 1 };
}

/**
 * Mirror the account's configured threshold, refreshed on every pane open from
 * /outlook/auth/context. Pass null when the account has no desktop configuration so
 * a stale mirror never outlives it.
 */
export function setAccountThreshold(record) {
  return new Promise((resolve) => {
    if (record) Office.context.roamingSettings.set(THRESHOLD_KEY, JSON.stringify(record));
    else Office.context.roamingSettings.remove(THRESHOLD_KEY);
    Office.context.roamingSettings.saveAsync(() => resolve());
  });
}
